import {
  BaseDiscount,
  PricesRegion,
  PriceValue,
  RegionConfig,
} from '@ca/common/types'

// Generic type for a price that can be discounted (even if it's not discounted yet)
export type Discountable = {
  base: number
  applied?: BaseDiscount[]
  discounted?: number
}

export type DiscountableItem<T extends { price: number }> = Omit<T, 'price'> & {
  price: Discountable
}

export type ItemWithPrice = {
  price: Discountable
}

/**
 * Creates a DiscountablePrice object with the given price and optional discounted price
 */
const wrap = (
  price: number,
  discounted?: number,
  applied: BaseDiscount[] = []
): Discountable => {
  // If the base price is 0, theres no point in having a discounted price, negative prices are not allowed
  if (price <= 0) {
    return { base: 0, applied, discounted: undefined }
  }
  // If the discounted price is equal to the base price, there's no point in having a discounted price
  if (discounted != null && price === discounted) {
    return { base: price, applied, discounted: undefined }
  }
  return {
    base: price,
    applied,
    discounted: discounted != null ? Math.max(discounted, 0) : undefined,
  }
}

/**
 * Unwraps the price from a DiscountablePrice object (returns the discounted price if it exists, otherwise the base price)
 */
const unwrap = (price: Discountable): number => price.discounted ?? price.base

const applyDiscount = (
  price: Discountable,
  discount: BaseDiscount
): Discountable => {
  const unwraped = unwrap(price)
  if (unwraped === 0) return price
  if (discount.type === 'percentage') {
    return wrap(
      price.base,
      unwraped - unwraped * (discount.amount / 100),
      price.applied ? [...price.applied, discount] : [discount]
    )
  }
  return wrap(
    price.base,
    unwraped - discount.amount,
    price.applied ? [...price.applied, discount] : [discount]
  )
}

export const removeDiscount = (price: Discountable) => {
  return wrap(price.base)
}

const applyDiscounts = (
  price: Discountable,
  discounts: BaseDiscount[] = []
) => {
  return discounts.reduce(applyDiscount, price)
}

export const applyDiscountsByType = (
  price: Discountable,
  discounts: BaseDiscount[] = [],
  type: BaseDiscount['type']
) => {
  return applyDiscounts(
    price,
    discounts.filter((discount) => discount.type === type)
  )
}

const sumDiscountablePrices = (a: Discountable, b: Discountable) =>
  wrap(a.base + b.base, unwrap(a) + unwrap(b))

const sumDiscountablePricesArr = (prices: Discountable[]) => {
  return prices.reduce(sumDiscountablePrices, wrap(0))
}

const isDiscounted = (price: Discountable) =>
  price.discounted != null && price.base !== unwrap(price)

const sumItemsWithPrices = (items: Iterable<ItemWithPrice>) => {
  const prices = [...items].map((item) => item.price)
  return sumDiscountablePricesArr(prices)
}

const wrapRegion = (price?: PricesRegion | null, region?: RegionConfig) => {
  const value = PriceValue.safe(price ?? undefined, region)
  return wrap(value)
}

export const Discounts = {
  wrap,
  wrapRegion,
  unwrap,
  isDiscounted,
  applyDiscount,
  applyDiscounts,
  applyDiscountsByType,
  sumDiscountablePrices,
  sumDiscountablePricesArr,
  sumItemsWithPrices,
}
